/**
 * @fileoverview Error state management hook for bootstrap operations.
 *
 * This module provides centralized error handling logic for the two-phase
 * bootstrap process, determining which errors should block the UI and which
 * are non-fatal.
 *
 * ## Error Classification
 *
 * The bootstrap process distinguishes between two types of errors:
 *
 * 1. **Critical Errors (FATAL)** - Block UI rendering
 *    - Authentication failures (401)
 *    - Network errors during critical fetch
 *    - Server errors (5xx) during critical fetch
 *    - Timeout during critical fetch
 *    - Invalid response format during critical fetch
 *
 * 2. **Cache Errors (NON-FATAL)** - Log as warnings only
 *    - Any error during cache fetch (/api/bootstrap/cache)
 *    - The app can function without items, spells, treasures, etc.
 *    - Users may notice missing reference data but core features work
 *
 * ## Usage
 *
 * ```tsx
 * function BootstrapHandler() {
 *   const { state } = useBootstrapContext();
 *   const { isError, error, retry } = useBootstrapError(
 *     state.criticalError,
 *     state.cacheError
 *   );
 *
 *   if (isError) {
 *     return <ErrorScreen error={error} onRetry={retry} />;
 *   }
 *
 *   return <MainApp />;
 * }
 * ```
 *
 * @see react-ui/src/hooks/bootstrap/types.ts for BootstrapErrorState type
 * @see react-ui/src/hooks/bootstrap/BootstrapProvider.tsx for error state management
 * @see docs/adr/0002-bootstrap-state-hydration.md for architecture decisions
 */

import { useEffect, useMemo } from 'react';
import { useBootstrapContext } from './BootstrapProvider';
import { logger } from '../../services/logger.client';

// =============================================================================
// Return Type
// =============================================================================

/**
 * Return type for the useBootstrapError hook.
 *
 * Provides a simplified interface for components to handle bootstrap errors
 * without needing to understand the distinction between critical and cache errors.
 */
export interface BootstrapErrorResult {
  /**
   * True if a FATAL error occurred that should block UI rendering.
   *
   * This is ONLY true for critical bootstrap errors. Cache errors are
   * non-fatal and do NOT set this flag - they are logged as warnings instead.
   *
   * When true, the application should:
   * - Display an error screen instead of the main UI
   * - Provide a retry button using the `retry` function
   * - Optionally provide a link to contact support
   */
  isError: boolean;

  /**
   * The critical error object, or null if no fatal error occurred.
   *
   * This will be an instance of one of:
   * - `BootstrapTimeoutError` - Request timed out
   * - `RetryExhaustedError` - All retry attempts failed
   * - `ApiError` - Server returned an error response
   * - `Error` - Generic error (network failure, etc.)
   *
   * Use `error.message` for a human-readable error description.
   * Use `error.name` to determine the error type for custom handling.
   */
  error: Error | null;

  /**
   * Function to trigger a retry of the bootstrap process.
   *
   * This function:
   * 1. Aborts any in-progress bootstrap requests
   * 2. Resets all bootstrap state (loading, completion, errors)
   * 3. Creates a fresh AbortController for the new attempt
   *
   * After calling this function, the consumer should trigger the bootstrap
   * fetch again. The BootstrapProvider's `retryBootstrap` action prepares
   * the state but does NOT automatically start the fetch.
   *
   * @example
   * ```tsx
   * function ErrorScreen({ error, retry }: { error: Error; retry: () => void }) {
   *   return (
   *     <div>
   *       <h1>Initialization Failed</h1>
   *       <p>{error.message}</p>
   *       <button onClick={retry}>Try Again</button>
   *     </div>
   *   );
   * }
   * ```
   */
  retry: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook for centralized bootstrap error handling.
 *
 * Aggregates errors from both bootstrap phases and determines whether
 * they should block the UI (critical errors) or just be logged (cache errors).
 *
 * ## Error Handling Strategy
 *
 * - **Critical errors** set `isError: true` and provide the error object
 * - **Cache errors** are logged as warnings but do NOT block the UI
 * - The `retry` function is always provided, regardless of error state
 *
 * ## Integration with BootstrapContext
 *
 * This hook uses `useBootstrapContext()` to access the `retryBootstrap` action.
 * It must be used within a `<BootstrapProvider>` component tree.
 *
 * @param criticalError - Error from critical bootstrap phase, or null
 * @param cacheError - Error from cache bootstrap phase, or null
 * @returns Object with `isError`, `error`, and `retry` properties
 *
 * @throws Error if used outside of BootstrapProvider
 *
 * @example
 * ```tsx
 * function AppInitializer() {
 *   const { state } = useBootstrapContext();
 *   const { isError, error, retry } = useBootstrapError(
 *     state.criticalError,
 *     state.cacheError
 *   );
 *
 *   // Show error screen for fatal errors
 *   if (isError && error) {
 *     return (
 *       <div className="error-screen">
 *         <h1>Something went wrong</h1>
 *         <p>{error.message}</p>
 *         <button onClick={retry}>Retry</button>
 *       </div>
 *     );
 *   }
 *
 *   // Normal rendering - cache errors don't block this
 *   return <MainApp />;
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Using with bootstrap context for custom error handling
 * function useBootstrapWithRetry() {
 *   const { state } = useBootstrapContext();
 *   const { isError, error, retry } = useBootstrapError(
 *     state.criticalError,
 *     state.cacheError
 *   );
 *
 *   return {
 *     isLoading: state.isCriticalLoading,
 *     isCacheLoading: state.isCacheLoading,
 *     isError,
 *     error,
 *     retry,
 *   };
 * }
 * ```
 */
export function useBootstrapError(
  criticalError: Error | null,
  cacheError: Error | null
): BootstrapErrorResult {
  // Get retry action from context
  const { retryBootstrap } = useBootstrapContext();

  // Log cache errors as warnings (non-fatal)
  // This effect runs when cacheError changes
  useEffect(() => {
    if (cacheError) {
      logger.warn(
        {
          errorName: cacheError.name,
          errorMessage: cacheError.message,
          errorStack: cacheError.stack?.split('\n').slice(0, 3).join('\n'),
        },
        'useBootstrapError: Cache bootstrap failed (non-fatal). ' +
          'Some features may be limited until cache data is available.'
      );
    }
  }, [cacheError]);

  // Log critical errors at error level (fatal)
  // This effect runs when criticalError changes
  useEffect(() => {
    if (criticalError) {
      logger.error(
        {
          errorName: criticalError.name,
          errorMessage: criticalError.message,
          errorStack: criticalError.stack?.split('\n').slice(0, 5).join('\n'),
        },
        'useBootstrapError: Critical bootstrap failed (FATAL). ' +
          'Application cannot proceed without critical data.'
      );
    }
  }, [criticalError]);

  // Memoize the result to prevent unnecessary re-renders
  // The result only changes when criticalError changes (cacheError doesn't affect output)
  const result = useMemo<BootstrapErrorResult>(() => {
    return {
      // Only critical errors are considered fatal
      isError: criticalError !== null,

      // Return the critical error (cache errors are not exposed)
      error: criticalError,

      // Retry action from context
      retry: retryBootstrap,
    };
  }, [criticalError, retryBootstrap]);

  // Debug log the result when it changes
  useEffect(() => {
    logger.debug(
      {
        isError: result.isError,
        hasError: result.error !== null,
        hasCacheError: cacheError !== null,
      },
      'useBootstrapError: Computed error state'
    );
  }, [result.isError, result.error, cacheError]);

  return result;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Determines if an error is a network-related error.
 *
 * This utility can be used by error display components to show
 * appropriate messaging (e.g., "Check your internet connection").
 *
 * @param error - The error to check
 * @returns True if the error appears to be network-related
 *
 * @example
 * ```tsx
 * function ErrorMessage({ error }: { error: Error }) {
 *   if (isNetworkError(error)) {
 *     return <p>Please check your internet connection and try again.</p>;
 *   }
 *   return <p>{error.message}</p>;
 * }
 * ```
 */
export function isNetworkError(error: Error): boolean {
  // TypeError is a common indicator of network errors
  if (error instanceof TypeError) {
    return true;
  }

  // Check for common network error message patterns
  // Using lowercase comparison for case-insensitive matching
  const message = error.message.toLowerCase();

  // Check each pattern individually for compatibility with lint rules
  if ((message ?? []).indexOf('network') !== -1) return true;
  if ((message ?? []).indexOf('failed to fetch') !== -1) return true;
  if ((message ?? []).indexOf('net::') !== -1) return true;
  if ((message ?? []).indexOf('econnrefused') !== -1) return true;
  if ((message ?? []).indexOf('enotfound') !== -1) return true;
  if ((message ?? []).indexOf('offline') !== -1) return true;

  return false;
}

/**
 * Determines if an error is a timeout error.
 *
 * This utility can be used by error display components to show
 * appropriate messaging (e.g., "The server took too long to respond").
 *
 * @param error - The error to check
 * @returns True if the error is a BootstrapTimeoutError
 *
 * @example
 * ```tsx
 * function ErrorMessage({ error }: { error: Error }) {
 *   if (isTimeoutError(error)) {
 *     return <p>The server is taking too long to respond. Please try again.</p>;
 *   }
 *   return <p>{error.message}</p>;
 * }
 * ```
 */
export function isTimeoutError(error: Error): boolean {
  return error.name === 'BootstrapTimeoutError';
}

/**
 * Determines if an error indicates that retries were exhausted.
 *
 * This utility can be used by error display components to show
 * appropriate messaging (e.g., "Multiple attempts failed").
 *
 * @param error - The error to check
 * @returns True if the error is a RetryExhaustedError
 *
 * @example
 * ```tsx
 * function ErrorMessage({ error }: { error: Error }) {
 *   if (isRetryExhaustedError(error)) {
 *     return <p>Unable to connect after multiple attempts. Please try again later.</p>;
 *   }
 *   return <p>{error.message}</p>;
 * }
 * ```
 */
export function isRetryExhaustedError(error: Error): boolean {
  return error.name === 'RetryExhaustedError';
}

/**
 * Gets a user-friendly error message based on the error type.
 *
 * Provides appropriate messaging for different error scenarios
 * without exposing technical details to users.
 *
 * @param error - The error to get a message for
 * @returns A user-friendly error message string
 *
 * @example
 * ```tsx
 * function ErrorScreen({ error }: { error: Error }) {
 *   return (
 *     <div>
 *       <h1>Oops!</h1>
 *       <p>{getUserFriendlyErrorMessage(error)}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function getUserFriendlyErrorMessage(error: Error): string {
  if (isNetworkError(error)) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  if (isTimeoutError(error)) {
    return 'The server is taking too long to respond. This might be due to high load. Please try again in a moment.';
  }

  if (isRetryExhaustedError(error)) {
    return 'We were unable to load the application after several attempts. The server might be experiencing issues. Please try again later.';
  }

  // Check for specific API error status codes
  if ('status' in error && typeof (error as { status: unknown }).status === 'number') {
    const status = (error as { status: number }).status;

    if (status === 401) {
      return 'Your session has expired. Please log in again.';
    }

    if (status === 403) {
      return 'You do not have permission to access this resource.';
    }

    if (status === 404) {
      return 'The requested resource was not found.';
    }

    if (status >= 500) {
      return 'The server encountered an error. Please try again later.';
    }
  }

  // Default message - use the error's own message if it's not too technical
  const message = error.message;
  if (message && message.length < 200 && !message.includes('at ') && !message.includes('Stack:')) {
    return message;
  }

  return 'An unexpected error occurred. Please try again.';
}
